import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { type RootState } from "app";

export type ToolbarTab = "general" | "tools";

interface BottomToolbarState {
    /** Currently selected tab */
    activeTab: ToolbarTab;
    /** Whether the extension panel is visible */
    isExpanded: boolean;
    /** Whether the floating Render modes menu is visible above the toolbar */
    isRenderMenuOpen: boolean;
    /**
     * Dev/QA flag (no in-app toggle): when true, the Render modes menu surfaces
     * every subtree row (including those reported as `Unavailable` by the
     * current scene) and the pencil button next to Points Cloud is shown so the
     * Point Cloud Settings drawer can be reached even on a scene without point
     * cloud data. Default is `false`, matching the desktop SubtreesPopover.
     * Flip via Redux DevTools or by editing the initial state.
     */
    showUnavailableModes: boolean;
}

const initialState: BottomToolbarState = {
    activeTab: "general",
    isExpanded: false,
    isRenderMenuOpen: false,
    showUnavailableModes: false,
};

export const bottomToolbarSlice = createSlice({
    name: "bottomToolbar",
    initialState,
    reducers: {
        /**
         * Toggle a tab:
         * - If collapsed → expand and set to this tab
         * - If expanded and same tab → collapse
         * - If expanded and different tab → collapse first (to animate out), then switch tab
         *   (The component will handle re-expanding after animation completes)
         */
        toggleTab: (state, action: PayloadAction<ToolbarTab>) => {
            const tab = action.payload;

            if (!state.isExpanded) {
                // Currently collapsed, expand and set tab
                state.isExpanded = true;
                state.activeTab = tab;
            } else if (state.activeTab === tab) {
                // Same tab clicked, collapse
                state.isExpanded = false;
            } else {
                // Different tab clicked - collapse first to animate out, then switch tab
                state.isExpanded = false;
                state.activeTab = tab;
            }
        },

        /**
         * Set the active tab directly
         */
        setActiveTab: (state, action: PayloadAction<ToolbarTab>) => {
            state.activeTab = action.payload;
        },

        /**
         * Expand the toolbar extension
         */
        expand: (state) => {
            state.isExpanded = true;
        },

        /**
         * Collapse the toolbar extension
         */
        collapse: (state) => {
            state.isExpanded = false;
        },

        /**
         * Open the floating Render modes menu (positioned above the bottom toolbar).
         */
        openRenderMenu: (state) => {
            state.isRenderMenuOpen = true;
        },

        /**
         * Close the floating Render modes menu.
         */
        closeRenderMenu: (state) => {
            state.isRenderMenuOpen = false;
        },

        /**
         * Toggle the floating Render modes menu.
         */
        toggleRenderMenu: (state) => {
            state.isRenderMenuOpen = !state.isRenderMenuOpen;
        },

        /**
         * Dev/QA: toggle visibility of unavailable subtree rows + pencil button.
         */
        toggleShowUnavailableModes: (state) => {
            state.showUnavailableModes = !state.showUnavailableModes;
        },

        /**
         * Dev/QA: set visibility of unavailable subtree rows + pencil button.
         */
        setShowUnavailableModes: (state, action: PayloadAction<boolean>) => {
            state.showUnavailableModes = action.payload;
        },

        /**
         * Reset to initial state
         */
        reset: () => initialState,
    },
});

export const bottomToolbarActions = bottomToolbarSlice.actions;
export const bottomToolbarReducer = bottomToolbarSlice.reducer;

// Selectors
const selectBottomToolbarState = (state: RootState) => state.bottomToolbar;

export const selectActiveTab = createSelector(selectBottomToolbarState, (state) => state.activeTab);

export const selectIsExpanded = createSelector(selectBottomToolbarState, (state) => state.isExpanded);

export const selectIsRenderMenuOpen = createSelector(selectBottomToolbarState, (state) => state.isRenderMenuOpen);

export const selectShowUnavailableModes = createSelector(
    selectBottomToolbarState,
    (state) => state.showUnavailableModes,
);
